import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import {
  AgentStatus,
  FlightParameters,
  FlightSearchResults,
  HotelSearchResults,
  searchMultiAgents,
  SearchStreamResponse,
  updateUserFeedback,
} from './api'

export type MessageSender = 'user' | 'agent'

export interface Message {
  id: string
  text: string
  sender: MessageSender
  timestamp: number
  traceIds?: string[]
  previousMessageId?: string
  isCard?: boolean
  flightParams?: FlightParameters
  flightSearchResults?: FlightSearchResults
  hotelResults?: HotelSearchResults // Will contain hotel results data when available
}

export interface Settings {
  useMockData: boolean
  langfuseDebugMode: boolean
  baseUrl: string
  langfuseBaseUrl: string
}

// Define the store state type
export interface VelaStore {
  // State
  isOpen: boolean
  messages: Message[]
  isLoading: boolean
  settings: Settings
  sessionId: string
  currentAgentStatus: AgentStatus | null
  abortController: AbortController | null

  // Actions
  open: () => void
  close: () => void
  toggle: () => void
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void
  setLoading: (isLoading: boolean) => void
  deleteMessagesFrom: (messageId: string) => void
  setAgentStatus: (status: AgentStatus | null) => void
  setAbortController: (controller: AbortController | null) => void

  sendMessage: (text: string, afterChatId?: string) => Promise<void>
  updateUserFeedback: (messageId: string, isHelpful: boolean, comment?: string) => Promise<void>

  // Settings actions
  updateSettings: (newSettings: Partial<Settings>) => void
}

// Generate a unique ID for messages
const generateId = (): string => Math.random().toString(36).substring(2, 15)

// Load settings from localStorage
const loadSettings = (): Settings => {
  const defaultSettings: Settings = {
    useMockData: false,
    langfuseDebugMode: false,
    baseUrl: 'https://dev.kyte.today',
    langfuseBaseUrl: 'https://cloud.langfuse.com/project/cmax4pc8t006vad07ccextgyn',
  }
  try {
    const savedSettings = localStorage.getItem('vela_widget_settings')
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings)
      // Ensure baseUrl is set even if it's not in the saved settings
      return {
        ...defaultSettings,
        ...parsedSettings,
      }
    }
  } catch (error) {
    console.error('Error loading settings from localStorage:', error)
  }
  return defaultSettings
}

// Save settings to localStorage
const saveSettings = (settings: Settings): void => {
  try {
    localStorage.setItem('vela_widget_settings', JSON.stringify(settings))
  } catch (error) {
    console.error('Error saving settings to localStorage:', error)
  }
}

// Create the store with immer middleware
export const useVelaStore = create<VelaStore>()(
  immer((set, get) => ({
    // Initial state
    isOpen: false,
    messages: [],
    isLoading: false,
    settings: loadSettings(),
    sessionId: generateId(),
    currentAgentStatus: null,
    abortController: null,

    // Actions
    open: () =>
      set(state => {
        state.isOpen = true
      }),
    close: () =>
      set(state => {
        state.isOpen = false
      }),
    toggle: () =>
      set(state => {
        state.isOpen = !state.isOpen
      }),
    setAgentStatus: (status: AgentStatus | null) =>
      set(state => {
        state.currentAgentStatus = status
      }),
    setAbortController: (controller: AbortController | null) =>
      set(state => {
        state.abortController = controller
      }),
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    sendMessage: async (text, afterChatId?: string): Promise<void> => {
      if (text.trim() === '') return

      const currentState = get()
      const previousMessageId =
        currentState.messages.length > 0
          ? currentState.messages[currentState.messages.length - 1].id
          : undefined

      const userMessage: Message = {
        id: generateId(),
        text,
        sender: 'user',
        timestamp: Date.now(),
        previousMessageId,
      }

      set(state => {
        state.messages.push(userMessage)
        state.isLoading = true
        state.currentAgentStatus = null
      })

      try {
        const updatedState = get()
        const { useMockData, baseUrl } = updatedState.settings

        // Create abort controller for this request
        const abortController = new AbortController()
        set(state => {
          state.abortController = abortController
        })

        const apiResponse = await searchMultiAgents(
          baseUrl,
          text,
          updatedState.messages,
          updatedState.sessionId,
          useMockData,
          afterChatId,
          (streamResponse: SearchStreamResponse) => {
            // Update agent status in real-time
            set(state => {
              state.currentAgentStatus = streamResponse.status
            })
          },
          abortController.signal,
        )

        const responseText = apiResponse.textResponse
        const hasFlightResults =
          !!apiResponse.flightResults &&
          ((!!apiResponse.flightResults.flights && apiResponse.flightResults.flights.length > 0) ||
            (apiResponse.flightResults.isFlexibleSearch &&
              !!apiResponse.flightResults.groupedFlights &&
              apiResponse.flightResults.groupedFlights.length > 0))
        const hasHotelResults =
          !!apiResponse.hotelResults && Object.keys(apiResponse.hotelResults).length > 0
        const isCard = hasFlightResults || hasHotelResults

        // Update user message ID with userQueryId from API response
        set(state => {
          // Find the user message we just added
          const userMessageIndex = state.messages.findIndex(msg => msg.id === userMessage.id)

          if (userMessageIndex !== -1) {
            // Update the user message ID
            state.messages[userMessageIndex].id = apiResponse.userQueryId
          }

          state.messages.push({
            id: apiResponse.responseId || generateId(),
            traceIds: apiResponse.traceIds,
            timestamp: Date.now(),
            text: responseText,
            sender: 'agent',
            previousMessageId: apiResponse.userQueryId, // Set previous message ID to the updated user message ID
            isCard,
            flightSearchResults: apiResponse.flightResults,
            flightParams: apiResponse.flightParams,
            hotelResults: apiResponse.hotelResults,
          })
          state.isLoading = false
          state.currentAgentStatus = null
          state.abortController = null
        })
      } catch (error) {
        console.error('Error sending message:', error)

        // Don't show error message if request was aborted
        if (error instanceof Error && error.name === 'AbortError') {
          set(state => {
            state.isLoading = false
            state.currentAgentStatus = null
            state.abortController = null
          })
          return
        }

        set(state => {
          state.messages.push({
            id: generateId(),
            timestamp: Date.now(),
            text: 'Sorry, there was an error processing your request. Please try again later.',
            sender: 'agent',
            previousMessageId: userMessage.id,
          })
          state.isLoading = false
          state.currentAgentStatus = null
          state.abortController = null
        })
      }
    },
    addMessage: message =>
      set(state => {
        const newMessage: Message = {
          id: generateId(),
          timestamp: Date.now(),
          ...message,
        }
        state.messages.push(newMessage)
      }),
    setLoading: isLoading =>
      set(state => {
        state.isLoading = isLoading
      }),

    deleteMessagesFrom: messageId =>
      set(state => {
        const messageIndex = state.messages.findIndex(msg => msg.id === messageId)
        if (messageIndex !== -1) {
          state.messages = state.messages.slice(0, messageIndex)
        }
      }),

    updateUserFeedback: async (messageId, isHelpful, comment) => {
      try {
        const { baseUrl } = get().settings
        await updateUserFeedback(baseUrl, messageId, isHelpful, comment)
      } catch (error) {
        console.error('Error updating feedback:', error)
        throw error
      }
    },

    // Settings actions
    updateSettings: newSettings =>
      set(state => {
        state.settings = { ...state.settings, ...newSettings }
        saveSettings(state.settings)
      }),
  })),
)
